import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useAuth } from '../features/auth/use-auth.js';
import { Button } from '../components/ui/button.js';
import { HestiaLogo } from '../components/hestia-logo.js';

const QuerySchema = z.object({
  token: z.string().min(40).max(128).optional(),
});

// Magic-link tokens are single-use. A component-level ref is NOT enough: React
// StrictMode (dev) mounts the component twice with independent refs, so the
// second mount would redeem the already-consumed token and surface a spurious
// error. Cache the in-flight redemption per token at module scope so both
// mounts share the same single network call.
const redemptions = new Map<string, Promise<{ ok: boolean; error?: string }>>();

function VerifyPage() {
  const search = Route.useSearch();
  const { verifyMagicLink } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Like /login, this is a light surface — force light so a dark-mode session
  // doesn't leave light-on-light invisible text. Restored on unmount.
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-theme');
    root.removeAttribute('data-theme');
    return () => {
      if (previous) root.setAttribute('data-theme', previous);
    };
  }, []);

  useEffect(() => {
    const token = search.token;
    if (!token) {
      setError('Hiányzik a token a linkből.');
      return;
    }
    let active = true;
    let pending = redemptions.get(token);
    if (!pending) {
      pending = verifyMagicLink(token);
      redemptions.set(token, pending);
    }
    void pending.then((res) => {
      if (!active) return;
      if (res.ok) {
        setDone(true);
        void navigate({ to: '/app' });
      } else {
        setError(res.error ?? 'Érvénytelen vagy lejárt link.');
      }
    });
    return () => {
      active = false;
    };
  }, [search.token, verifyMagicLink, navigate]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="hestia-marketing-rise w-full max-w-md text-center">
        <div className="mb-6 flex justify-center">
          <HestiaLogo size={40} />
        </div>
        <div className="h2-eyebrow" style={{ justifyContent: 'center' }}>
          Belépés ellenőrzése
        </div>
        <h1 className="mt-2 font-serif text-display font-normal leading-[1.05] tracking-[-0.02em] text-[var(--ink-700)]">
          {error ? (
            <>
              Hoppá<span className="text-[var(--danger)]">.</span>
            </>
          ) : done ? (
            <>
              Beléptél<span className="text-[var(--brand-500)]">.</span>
            </>
          ) : (
            <>
              Egy pillanat<span className="text-[var(--brand-500)]">…</span>
            </>
          )}
        </h1>
        <p className="mx-auto mt-3 max-w-[320px] text-body text-[var(--fg-2)]">
          {error
            ? error
            : done
              ? 'Sikeres bejelentkezés. Átirányítunk a munkafelületre…'
              : 'Ellenőrizzük a belépési linkedet.'}
        </p>
        {error && (
          <Button className="mt-5" onClick={() => navigate({ to: '/login' })}>
            Vissza a bejelentkezéshez
          </Button>
        )}
      </div>
    </main>
  );
}

export const Route = createFileRoute('/login_/verify')({
  validateSearch: (search) => QuerySchema.parse(search),
  component: VerifyPage,
});
